import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import { config } from 'config';

import type { AppState } from 'index';

import { getListOfLinkDataForPathname } from 'utils/getListOfLinkDataForPathname';

/**
 * HreflangMeta Component
 *
 * This component manages hreflang meta tags for SEO purposes.
 *
 * IMPORTANT: Why this custom component exists:
 *
 * React Helmet has a known issue with managing link tags during client-side navigation.
 * When navigating between pages in a SPA, React Helmet appends new hreflang tags
 * instead of replacing the existing ones. This causes the tags to accumulate:
 * - Initial page load: 3 hreflang tags (uz, ru, x-default)
 * - After first navigation: 6 tags (duplicates)
 * - After second navigation: 9 tags (triplicates)
 * ... and so on
 *
 * This component solves the issue by:
 * 1. Tracking whether it's the first render (SSR or initial page load)
 * 2. On subsequent renders (client-side navigation), it cleans up duplicate tags
 * 3. Uses setTimeout(0) to ensure cleanup happens after Helmet adds new tags
 * 4. Keeps only the last 3 hreflang tags (the newest ones)
 *
 * This ensures we always have exactly 3 hreflang tags as required by the SEO spec.
 */
export const HreflangMeta = () => {
  const seo = useSelector((state: AppState) => state.seo);
  const { pathname } = useSelector((state: AppState) => state.router.location);
  const region = useSelector((state: AppState) => state.app.currentRegion);
  const search = useSelector((state: AppState) => state.router.location.search);

  const defaultLinks = getListOfLinkDataForPathname(pathname, { region, search });
  const links = seo.links || defaultLinks;

  useEffect(() => {
    // For subsequent renders (client-side navigation), we need to clean up duplicates
    // Use setTimeout to defer cleanup until after React Helmet has added the new tags
    // This is necessary because React Helmet adds tags asynchronously
    const timeoutId = setTimeout(() => {
      try {
        const existingHreflangs = document.querySelectorAll('link[rel="alternate"][hreflang]');

        // Keep only the last 3 hreflang tags (the newest ones)
        // We expect exactly 3: one for 'uz', one for 'ru', and one for 'x-default'
        if (existingHreflangs.length > 3) {
          // Remove the older duplicates, keeping the newest ones
          for (let i = 0; i < existingHreflangs.length - 3; i += 1) {
            existingHreflangs[i].remove();
          }
        }
      } catch (error) {
        Sentry.captureException('Failed to cleanup hreflang tags:', {
          level: 'warning',
          extra: { error },
        });
      }
    }, 0);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeoutId);
  }, [links]); // Re-run when links change (on navigation)

  return (
    <Helmet>
      {links.map((item) => (
        <link
          key={`hreflang-${item.hrefLang}`}
          rel="alternate"
          hrefLang={item.hrefLang}
          href={item.href?.startsWith('http') ? item.href : `${config.ENV.ORIGIN_URL}${item.href}`}
        />
      ))}
    </Helmet>
  );
};
